import React, { useEffect, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';
import { levelAtLeast, resetCapabilityCache } from '../components/useAdminCapability';
import { clearAccessCache } from '../auth/pageAccess';
import './AdminLogin.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

// Ordered tab -> landing route map used to send a Staff user to the first tab
// they have been granted (View or Edit). Ordering mirrors the admin nav so the
// landing page is predictable. The backend remains the security boundary; this
// only decides where to drop the user after a successful sign-in.
const STAFF_TAB_ROUTES = [
  ['General Settings', '/admin/general'],
  ['Products', '/admin/dashboard'],
  ['Categories', '/admin/categories'],
  ['Orders/Reports', '/admin/reports/orders'],
  ['POS Queue', '/admin/channels/pos/queue'],
  ['Locations', '/admin/locations'],
  ['Discounts', '/admin/discounts'],
  ['Reviews', '/admin/reviews'],
  ['Theme', '/admin/theme'],
  ['Audit', '/admin/audit'],
];

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Forced-password-change (first sign-in) state. When Cognito returns the
  // NEW_PASSWORD_REQUIRED challenge for an invited user, we hold the challenged
  // user object and switch the form to "set a new password".
  const [step, setStep] = useState('login'); // 'login' | 'newPassword'
  const [challengedUser, setChallengedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const statusRef = useRef(null);
  const navigate = useNavigate();

  // Whenever the login page is shown (initial load, logout, or an
  // unauthorized bounce), wipe any cached capability from a previous user so
  // the next sign-in never inherits the prior user's role/permissions (which
  // otherwise showed stale, elevated tabs until a hard refresh).
  useEffect(() => {
    resetCapabilityCache();
    clearAccessCache();
  }, []);

  useEffect(() => {
    if (!error) return;
    try {
      window.setTimeout(() => {
        try {
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
          statusRef.current?.focus?.({ preventScroll: true });
        } catch {
          // ignore
        }
      }, 50);
    } catch {
      // ignore
    }
  }, [error]);

  // Resolves the authenticated caller's authorization and navigates to the
  // right landing page. Backward-compatible: existing Admins/PosAdmins are
  // routed by their Cognito group. General Staff (no group) are authorized via
  // the authoritative /admin/users/me capability. Returns true when the caller
  // was authorized and navigation happened; false otherwise (caller signed out).
  const authorizeAndRedirect = async () => {
    const session = await Auth.currentSession();
    const groups = session.getIdToken().payload['cognito:groups'] || [];
    const isAdmin = Array.isArray(groups) && groups.includes('Admins');
    const isPosAdmin = Array.isArray(groups) && groups.includes('PosAdmins');

    // Group-based fast path keeps existing behavior intact.
    if (isAdmin) {
      navigate('/admin/general');
      return true;
    }
    if (isPosAdmin) {
      navigate('/admin/channels/pos/queue');
      return true;
    }

    // No admin group: consult the authoritative role/permission capability.
    // This is how general Staff (who belong to no Cognito group) are granted
    // access under the new role model.
    try {
      if (API_BASE_URL) {
        const token = session.getIdToken().getJwtToken();
        const res = await fetch(`${API_BASE_URL}/admin/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          const role = data?.role;
          const permissions =
            data?.permissions && typeof data.permissions === 'object' ? data.permissions : {};

          if (role === 'Superadmin' || role === 'Admin') {
            navigate('/admin/general');
            return true;
          }
          if (role === 'Staff') {
            const landing = STAFF_TAB_ROUTES.find(([tab]) =>
              levelAtLeast(permissions[tab], 'View')
            );
            if (landing) {
              navigate(landing[1]);
              return true;
            }
          }
        }
      }
    } catch {
      // fall through to unauthorized
    }

    setError('Unauthorized: Admin access required');
    await Auth.signOut();
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const user = await Auth.signIn(username, password);

      // Invited users are created in FORCE_CHANGE_PASSWORD state, so the first
      // sign-in returns this challenge instead of completing. Switch to the
      // "set new password" step rather than failing.
      if (user?.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setChallengedUser(user);
        setStep('newPassword');
        return;
      }

      await authorizeAndRedirect();
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      // requiredAttributes is empty for these invited users (email is already
      // set + verified), so no extra attributes are passed.
      await Auth.completeNewPassword(challengedUser, newPassword);
      // Surface a brief confirmation before routing into the portal so the user
      // gets clear feedback that the password change actually took effect.
      setSuccess('Password updated successfully. Signing you in…');
      await new Promise((resolve) => window.setTimeout(resolve, 1200));
      await authorizeAndRedirect();
    } catch (err) {
      console.error('Set new password error:', err);
      setError(err.message || 'Failed to set new password');
    } finally {
      setLoading(false);
    }
  };

  const renderError = () =>
    error && (
      <div className="error-message" ref={statusRef} tabIndex={-1} role="alert" aria-live="assertive">
        <i className="fas fa-exclamation-circle"></i>
        {error}
      </div>
    );

  const renderSuccess = () =>
    success && (
      <div
        className="success-message"
        role="status"
        aria-live="polite"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 12px',
          marginBottom: '12px',
          borderRadius: '8px',
          background: '#ecfdf5',
          border: '1px solid #a7f3d0',
          color: '#047857',
          fontWeight: 600,
        }}
      >
        <i className="fas fa-check-circle"></i>
        {success}
      </div>
    );

  return (
    <div className="admin-login-container">
      <div className="admin-login-box">
        <div className="admin-login-header">
          <div className="admin-logo-text">
            <i className="fas fa-shield-alt"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <h2>Admin Portal</h2>
          <p>{step === 'newPassword' ? 'Set a new password' : 'Product Management System'}</p>
        </div>

        {step === 'newPassword' ? (
          <form onSubmit={handleSetNewPassword} className="admin-login-form">
            {renderError()}
            {renderSuccess()}

            <p className="admin-login-hint">
              This is your first sign-in. Choose a new password to finish setting up your account.
            </p>

            <div className="form-group">
              <label htmlFor="newPassword">
                <i className="fas fa-lock"></i>
                New password
              </label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter a new password"
                required
                autoComplete="new-password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">
                <i className="fas fa-lock"></i>
                Confirm password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter the new password"
                required
                autoComplete="new-password"
              />
            </div>

            <button type="submit" className="admin-login-btn" disabled={loading}>
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-check"></i>
                  Set Password & Sign In
                </>
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="admin-login-form">
            {renderError()}

            <div className="form-group">
              <label htmlFor="username">
                <i className="fas fa-user"></i>
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter admin email or username"
                required
                autoComplete="username"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">
                <i className="fas fa-lock"></i>
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                autoComplete="current-password"
              />
            </div>

            <button type="submit" className="admin-login-btn" disabled={loading}>
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Signing in...
                </>
              ) : (
                <>
                  <i className="fas fa-sign-in-alt"></i>
                  Sign In
                </>
              )}
            </button>
          </form>
        )}

        <div className="admin-login-footer">
          <p>
            <i className="fas fa-shield-alt"></i>
            Authorized personnel only
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
